import {
  FETCH_REDACTION_CODES_SUCCESS,
  FETCH_REDACTION_CODES_FAILURE,
  DELETE_REDACTION_CODE_SUCCESS,
  DELETE_REDACTION_CODE_FAILURE,
  SAVE_REDACTION_CODE_SUCCESS,
  SAVE_REDACTION_CODE_FAILURE,
  SET_REDACTION_CODE_DELETED_SUCCESS,
  SET_REDACTION_CODE_DELETED_FAILURE,
  CHECK_REDACTION_CODE_EXISTS_SUCCESS,
  CHECK_REDACTION_CODE_EXISTS_FAILURE,
  fetchCountRedactionCodesSuccess,
} from '../actions';
import {
  checkRedactionCodeExistsQuery,
  deleteRedactionCodeQuery,
  saveRedactionCodeQuery,
  RETRIEVE_REDACTION_CODES,
  fetchCountRedactionCodeQuery,
  FetchCountRedactionCodeQueryResponse,
  DeleteRedactionCodeQueryResponse,
} from './queries';
import { Action, ThunkAction, UnknownAction } from '@reduxjs/toolkit';
import {
  deleteReactionCodePayload,
  RedactionCode,
  SaveRedactionCodeSuccessPayload,
  FetchRedactionCodeQueryResponse,
} from '../models';
import callGraphQLApi, {
  GQLApiDispatch,
  GQLApiGetState,
} from '@helpers/callGraphQLApi';
import { lookupLatestRedactionCodesSchemaId } from '@cbsa-modules/universal';
import { omit } from 'lodash';
import { fetchGraphQLApi } from '@helpers/callApi';
import { modules } from '@veritone/glc-redux';

const {
  auth: { selectOAuthToken, selectSessionToken },
  config: { getConfig },
} = modules;
// export interface FetchRedactionCodesResponse {
//   offset: number;
//   searchMedia?: {
//     jsondata: {
//       results: Record<string, any>;
//     };
//   }[];
// }

export interface FetchRedactionCodesResponse {
  structuredDataObjects: {
    count: number;
    records: {
      id: string;
      createdDateTime: string;
      modifiedDateTime: string;
      data: RedactionCode;
    }[];
  };
}

const NOOP = 'noop';

type Thunk<P, R = any> = (
  payload: P,
  ...args: any[]
) => ThunkAction<Promise<R | undefined>, any, unknown, Action>;

export const fetchRedactionCodes =
  (limit: number, offset: number) =>
  async (dispatch: GQLApiDispatch, getState: GQLApiGetState) => {
    const schemaId = await lookupLatestRedactionCodesSchemaId(
      dispatch,
      getState
    );
    if (!schemaId) {
      return;
    }

    return await callGraphQLApi<FetchRedactionCodesResponse>({
      actionTypes: [
        NOOP,
        FETCH_REDACTION_CODES_SUCCESS,
        FETCH_REDACTION_CODES_FAILURE,
      ],
      query: RETRIEVE_REDACTION_CODES,
      variables: { schemaId, limit, offset },
      dispatch: (action) => {
        dispatch(
          FETCH_REDACTION_CODES_SUCCESS.match(action)
            ? { ...action, payload: { ...action.payload } }
            : action
        );
      },
      getState,
    });
  };

export const deleteRedactionCodeSdo: Thunk<deleteReactionCodePayload> =
  (payload) => async (dispatch, getState) => {
    const schemaId = await lookupLatestRedactionCodesSchemaId(
      dispatch,
      getState
    );
    if (!schemaId) {
      return;
    }
    const { sdoId } = payload;
    return await callGraphQLApi<DeleteRedactionCodeQueryResponse>({
      actionTypes: [
        NOOP,
        DELETE_REDACTION_CODE_SUCCESS,
        DELETE_REDACTION_CODE_FAILURE,
      ],
      query: deleteRedactionCodeQuery,
      variables: { sdoId, schemaId },
      dispatch,
      getState,
      throwOnError: true,
    });
  };

export const saveRedactionCode: Thunk<{
  redactionCode: RedactionCode;
}> = (payload) => async (dispatch, getState) => {
  const { redactionCode } = payload;
  const schemaId = await lookupLatestRedactionCodesSchemaId(dispatch, getState);
  if (!schemaId) {
    return;
  }

  return await callGraphQLApi<SaveRedactionCodeSuccessPayload>({
    actionTypes: [
      NOOP,
      SAVE_REDACTION_CODE_SUCCESS,
      SAVE_REDACTION_CODE_FAILURE,
    ],
    query: saveRedactionCodeQuery,
    variables: {
      id: redactionCode.id,
      schemaId,
      data: omit(redactionCode, ['createdDateTime', 'modifiedDateTime']),
    },
    dispatch,
    getState,
  });
};

/**
 * Write a code back with `isDeleted` flipped.
 *
 * The same upsert `saveRedactionCode` makes, but reporting through its own
 * actions: the save path closes the drawer and says "Saved Redaction Code
 * successfully", and a soft delete from the table is neither.
 */
export const setRedactionCodeDeleted: Thunk<{
  redactionCode: RedactionCode;
}> = (payload) => async (dispatch, getState) => {
  const { redactionCode } = payload;
  const schemaId = await lookupLatestRedactionCodesSchemaId(dispatch, getState);
  if (!schemaId) {
    return;
  }

  return await callGraphQLApi<SaveRedactionCodeSuccessPayload>({
    actionTypes: [
      NOOP,
      SET_REDACTION_CODE_DELETED_SUCCESS,
      SET_REDACTION_CODE_DELETED_FAILURE,
    ],
    query: saveRedactionCodeQuery,
    variables: {
      id: redactionCode.id,
      schemaId,
      data: omit(redactionCode, ['createdDateTime', 'modifiedDateTime']),
    },
    dispatch,
    getState,
  });
};

export const checkRedactionCodeExists: Thunk<{
  redactionCode: RedactionCode;
  isEdit: boolean;
}> = (payload) => async (dispatch, getState) => {
  const { redactionCode, isEdit } = payload;
  const schemaId = await lookupLatestRedactionCodesSchemaId(dispatch, getState);
  if (!schemaId) {
    return;
  }
  const oauthToken = selectOAuthToken(getState());
  const sessionToken = selectSessionToken(getState());
  const { apiRoot, graphQLEndpoint, veritoneAppId } =
    getConfig<Window['config']>(getState());
  const authorization = {
    endpoint: `${apiRoot}/${graphQLEndpoint}`,
    token: sessionToken || oauthToken,
    veritoneAppId,
  };
  try {
    const [existingCodesResponse, existingCodeNamesResponse] =
      await Promise.all([
        fetchGraphQLApi<FetchRedactionCodeQueryResponse>({
          ...authorization,
          query: checkRedactionCodeExistsQuery,
          variables: { filter: { code: redactionCode.code.trim() }, schemaId },
        }),
        fetchGraphQLApi<FetchRedactionCodeQueryResponse>({
          ...authorization,
          query: checkRedactionCodeExistsQuery,
          variables: {
            filter: { codeName: redactionCode.codeName.trim() },
            schemaId,
          },
        }),
      ]);

    const existingCodes =
      existingCodesResponse?.data?.structuredDataObjects?.records || [];
    const existingCodeNames =
      existingCodeNamesResponse?.data?.structuredDataObjects?.records || [];

    /**
     * A soft-deleted code does not reserve its name.
     *
     * The filter matches on `code` / `codeName` alone, so without this a
     * deleted code blocked re-creating one with the same name for good —
     * and the error named a row the user could only find in the deleted
     * view. Restoring it is the collision that matters, and the deleted
     * view guards that separately.
     */
    const isLive = (record: { data?: { isDeleted?: boolean } }) =>
      record?.data?.isDeleted !== true;

    let isDuplicate = false;
    if (isEdit) {
      const filteredCodes = existingCodes.filter(
        (code) => code.id !== redactionCode.id && isLive(code)
      );
      const filteredCodeNames = existingCodeNames.filter(
        (code) => code.id !== redactionCode.id && isLive(code)
      );
      isDuplicate = filteredCodes.length > 0 || filteredCodeNames.length > 0;
    } else {
      isDuplicate =
        existingCodes.filter(isLive).length > 0 ||
        existingCodeNames.filter(isLive).length > 0;
    }

    const results = {
      isDuplicate,
      redactionCode,
    };
    dispatch(CHECK_REDACTION_CODE_EXISTS_SUCCESS(results));
  } catch {
    dispatch(CHECK_REDACTION_CODE_EXISTS_FAILURE());
  }
};

export const fetchTotalRedactionCodes =
  () => async (dispatch: (a: UnknownAction) => void, getState: () => any) => {
    const schemaId = await lookupLatestRedactionCodesSchemaId(
      dispatch,
      getState
    );
    if (!schemaId) {
      return;
    }
    const oauthToken = selectOAuthToken(getState());
    const sessionToken = selectSessionToken(getState());
    const { apiRoot, graphQLEndpoint, veritoneAppId } =
      getConfig<Window['config']>(getState());
    const authorization = {
      endpoint: `${apiRoot}/${graphQLEndpoint}`,
      token: sessionToken || oauthToken,
      veritoneAppId,
    };

    let count = 0;
    let offset = 0;
    const limit = 100;
    let results = 0;
    do {
      const response =
        await fetchGraphQLApi<FetchCountRedactionCodeQueryResponse>({
          ...authorization,
          query: fetchCountRedactionCodeQuery,
          variables: { schemaId, limit, offset },
        });
      offset += limit;
      count = response?.data?.structuredDataObjects?.count ?? 0;
      results += count;
    } while (count === limit);
    dispatch(fetchCountRedactionCodesSuccess(results));
  };
